import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Fingerprint, Trash2, Pencil, PlusCircle, Check, X, Smartphone } from "lucide-react";
import { startRegistration } from "@simplewebauthn/browser";
import { api, errMsg } from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Card } from "./Section";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}

export default function PasskeyCard() {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [working, setWorking] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");

  const { data } = useQuery({
    queryKey: ["admin", "passkeys"],
    queryFn: () => api.get("/admin/passkey").then((r) => r.data),
  });
  const items = data?.items || [];
  const max = data?.max || 5;

  const register = async () => {
    if (!name.trim()) { toast.error("Önce bir cihaz adı yazın (örn. iPhone 15, MacBook)"); return; }
    if (!window.PublicKeyCredential) { toast.error("Tarayıcınız passkey desteklemiyor"); return; }
    setWorking(true);
    try {
      const begin = await api.post("/admin/passkey/register/begin").then((r) => r.data);
      const ceremonyId = begin.ceremonyId;
      const options = { ...begin };
      delete options.ceremonyId;
      const attestation = await startRegistration({ optionsJSON: options });
      await api.post("/admin/passkey/register/complete", {
        ...attestation,
        device_name: name.trim(),
      }, { headers: { "X-Ceremony-Id": ceremonyId } });
      toast.success("Cihaz kaydedildi — artık parmak izi ile giriş yapabilirsiniz");
      setName("");
      qc.invalidateQueries({ queryKey: ["admin", "passkeys"] });
    } catch (e) {
      if (e?.name === "NotAllowedError") toast.error("İşlem iptal edildi veya zaman aşımına uğradı");
      else toast.error(errMsg(e));
    } finally { setWorking(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu cihazı silmek istediğinize emin misiniz? Bu cihazdan artık parmak izi ile giriş yapamazsınız.")) return;
    try {
      await api.delete(`/admin/passkey/${id}`);
      toast.success("Cihaz silindi");
      qc.invalidateQueries({ queryKey: ["admin", "passkeys"] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const rename = async (id) => {
    if (!editName.trim()) return;
    try {
      await api.patch(`/admin/passkey/${id}`, { device_name: editName.trim() });
      setEditingId(null); setEditName("");
      qc.invalidateQueries({ queryKey: ["admin", "passkeys"] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const atMax = items.length >= max;

  return (
    <Card title="Parmak izi / Face ID girişi (Passkey)" sub={`En fazla ${max} cihaz kaydedebilirsiniz. Kayıttan sonra şifre ve TOTP olmadan parmak izi / Face ID ile tek dokunuşla giriş yaparsınız.`} testid="security-passkey">
      <div className="space-y-4" data-testid="passkey-manager">
        {/* Add new */}
        <div className="border rounded-lg p-4 bg-stone-50/60 space-y-3">
          <p className="text-sm font-medium flex items-center gap-2"><PlusCircle size={15} className="text-stone-700" />Yeni cihaz ekle</p>
          <p className="text-xs text-stone-500">Bu cihazda <strong>Face ID / parmak izi / Windows Hello</strong> açık olmalı. "Kaydet" dedikten sonra tarayıcı sizden biyometrik onay isteyecek.</p>
          <div className="flex items-center gap-2 flex-wrap">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Cihaz adı (iPhone 15, MacBook Pro, Samsung Fold…)" className="max-w-xs" data-testid="passkey-name-input" disabled={atMax} />
            <Button type="button" onClick={register} disabled={working || atMax || !name.trim()} data-testid="passkey-add-button">
              <Fingerprint size={14} className="mr-1" />{working ? "Bekleniyor…" : "Bu cihazı kaydet"}
            </Button>
          </div>
          {atMax && <p className="text-xs text-amber-700">En fazla {max} cihaz eklenebilir. Yeni bir cihaz eklemek için eski birini silin.</p>}
        </div>

        {/* List */}
        {items.length === 0 ? (
          <div className="text-sm text-stone-400 text-center py-6 border rounded-lg bg-white" data-testid="passkey-empty">
            <Fingerprint size={28} className="mx-auto mb-2 text-stone-300" />
            Henüz kayıtlı cihaz yok. Yukarıdan ilk passkey'i ekleyin.
          </div>
        ) : (
          <div className="border rounded-lg bg-white divide-y" data-testid="passkey-list">
            {items.map((p) => (
              <div key={p.id} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`passkey-row-${p.id}`}>
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <Smartphone size={18} className="text-stone-500 shrink-0" />
                  <div className="min-w-0 flex-1">
                    {editingId === p.id ? (
                      <div className="flex items-center gap-1">
                        <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-8 text-sm" data-testid={`passkey-rename-input-${p.id}`} />
                        <Button type="button" size="sm" variant="ghost" onClick={() => rename(p.id)} data-testid={`passkey-rename-confirm-${p.id}`}><Check size={14} /></Button>
                        <Button type="button" size="sm" variant="ghost" onClick={() => { setEditingId(null); setEditName(""); }}><X size={14} /></Button>
                      </div>
                    ) : (
                      <>
                        <p className="font-medium text-sm truncate" title={p.device_name}>{p.device_name}</p>
                        <p className="text-xs text-stone-500">Eklendi {fmtDate(p.created_at)}{p.last_used_at ? ` · Son kullanım ${fmtDate(p.last_used_at)}` : " · Henüz kullanılmadı"}</p>
                      </>
                    )}
                  </div>
                </div>
                {editingId !== p.id && (
                  <div className="flex items-center gap-1 shrink-0">
                    <Button type="button" size="sm" variant="ghost" onClick={() => { setEditingId(p.id); setEditName(p.device_name); }} data-testid={`passkey-rename-${p.id}`} aria-label="rename"><Pencil size={14} /></Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => remove(p.id)} data-testid={`passkey-delete-${p.id}`} aria-label="delete"><Trash2 size={14} className="text-red-600" /></Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
